<script>
export default {
	name: 'USP',
	inheritAttrs: false,
};
</script>

<script setup>
import FontAwesomeLazy from '~/components/FontAwesomeLazy.vue';
import AppHyperlink from '~/components/AppHyperlink.vue';
import { isMobileViewport, isLG } from '~/logic/composables/breakpoints';
import DOMPurify from '~/../custom-modules/dompurify/dompurify.js';


const props = defineProps({
	uspType: { type: String, default: '' },
	uspList: { type: Array, default: () => [] },
	componentIdAA: { type: String, default: '' },
});

/*
*	@customImgPath String parameter
*	@imageType String parameter
*
*	Return background image inline-style string
*	Otherwise return empty string
*/
const iconImgFunc = (customImgPath, imageType) => {
	if ( customImgPath !== null && imageType === 'icon-image') return `background-image: url(${customImgPath})`;
	return '';
};

/*
*	@ctaLinkType String parameter
*
*	Return boolean either open new tab or stay current page
*	when CTA clicked
*/
const isNewTab = (ctaLinkType) => {
	return ctaLinkType === 'openInNewTab' ? '_blank' : null;
};

const ctaAttributes = (href, ctaLinkType) => {

	if (ctaLinkType === 'downloadFile') {
		return {
			class: 'cta-btn inline-block',
			href,
			...(ctaLinkType === 'downloadFile' ? { download: '' } : null),
			...(ctaLinkType === 'openInNewTab' ? { target: '_blank' } : null),
		};
	} else {
		return {
			class: 'cta-btn inline-block',
			href,
			showExternalLinkIcon: false,
			...(ctaLinkType === 'openInNewTab' ? { target: '_blank' } : null),
		};
	}
};

const getTextContentFromHtmlString = (htmlString) => {
	/*
		`<h5 style="text-align: center;">MHexplorer</h5><p style="text-align: center;">Live like a student. Travel the world like a VIP. Sign up to our student travel and lifestyle programme to enjoy amazing benefits.</p>`
		^ Expected output from above sample string:
		'MHexplorer Live like a student. Travel the world like a VIP. Sign up to our student travel and lifestyle programme to enjoy amazing benefits. '
	*/
	const modifiedString = htmlString.replace(/<\/(.{1,}?)>/gi, ' </$1>');
	return DOMPurify.sanitize(modifiedString, { ALLOWED_TAGS: [] });
};

const isSongketPatternSelected = (songketOptionValue) => {
	const magSongketOptions = [
		'mag-songket-blue',
		'mag-songket-teal',
		'mag-songket-gold',
	];
	
	return magSongketOptions.includes(songketOptionValue);
};

const siteName = window.siteName;

</script>

<template>
<div
	class="USP"
	:class="{ 'type-2': props.uspType === 'type2' }"
	:data-use-theme="siteName"
>
	<div class="USPBlock">
		<ul
			class="USPList "
		>
			<li
				v-for="(item, index) in props.uspList"
				:key="index"
				class="USPItem"
				:class="{ '>lg:flex-col': props.uspType === 'type1' }"
			>
				<div class="">
					<div
						class="USPItemIcon relative"
						:class="[
							item.batikBackground,
							{
								'type-2': props.uspType === 'type2',
								'USPItemIconSongketPattern': isSongketPatternSelected(item.batikBackground),
							},
						]"
						:style="[iconImgFunc( item.iconImage, item.iconType )]"
					>
						<FontAwesomeLazy
							v-if="item.iconType === 'icon-font-awesome-id'"
							:iconId="item.iconId"
							:iconStyle="siteName !== 'firefly' ? 'regular' : 'light'"
							class="text-neutral-white-base"
							:class="[{ 'type-2': props.uspType === 'type2' }]"
						/>
					</div>
				</div>
				<div
					class="USPItemBody"
					:class="{
						'>lg:flex >lg:flex-col >lg:items-center >lg:px-3': props.uspType === 'type1',
						'USPBodyItemTextLeft': item.mobileTextAlignment === 'left' && (isMobileViewport || isLG),
						'USPBodyItemTextRight': item.mobileTextAlignment === 'right' && (isMobileViewport || isLG),
						'USPBodyItemTextCenter': item.mobileTextAlignment === 'centre' && (isMobileViewport || isLG),
					}"
				>
					<div
						v-html-sanitize="item.uspText"
						class="author-html-container USPItemBodyText USPBodyItemEnhanced"
						:class="props.uspType === 'type1' ? 'mb-6 md:mb-3' : 'mb-3'"
					></div>
					<component
						:is="item.ctaLinkType === 'downloadFile' ? 'a' : AppHyperlink"
						v-if="(item.ctaText && item.ctaURL)"
						v-aa="[
							{
								clickName: item.ctaText,
								clickComponentType: 'URL',
								componentName: 'USP',
								componentID: props.componentIdAA,
							},
							{
								name: item.ctaText,
								type: 'other',
							},
						]"
						v-bind="ctaAttributes(item.ctaURL, item.ctaLinkType)"
					>
						<span>{{ item.ctaText }}</span>
						<icon-far-arrow-up-right-from-square
							v-if="item.externalLinkIcon"
							role="presentation"
							class="text-base ml-4 inline-block fill-current"
						/>
						<icon-far-arrow-right
							v-else
							role="presentation"
							class="cta-btn-arrow-icon text-base ml-4 rtl:mr-4 inline-block"
						/>
						<div class="sr-only">
							{{ getTextContentFromHtmlString(item.uspText) }}
						</div>
					</component>
				</div>
			</li>
		</ul>
	</div>
</div>
</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.USP {
	@apply my-16;
	
	@media #{var.$query-min-lg} {
		padding-left: 123px;
		padding-right: 123px;
		@apply my-20;
	}

	&.type-2 {
		margin-top: 52px;
		
		.USPList {
			@apply md:flex grid grid-cols-2 >lg:gap-8;
		}

		.USPItem {
			@apply flex w-full;

			.USPItemIcon {
				@media #{var.$query-min-lg} {
				width: 110px;
				height: 143px;
				}

				svg {
					@media #{var.$query-min-lg} {
						font-size: 42px;
					}
				}
			}
			
			// type-2 songket pattern background stylings
			.USPItemIconSongketPattern {
				
				@media #{var.$query-min-lg} {
					background-size: 102px;
					background-position: 5px 68px;
				}
			}

			.USPItemBody {
				@apply text-left;
				display: flex;
				flex-direction: column;
				
				html[dir="rtl"] & {
					@apply mr-6 ml-0;
				}
				
				@media #{var.$query-min-lg} {
					@apply ml-6;
				}

				.title {
					@apply md:mb-[12px] mb-[17px];
				}
			}
		}
	}

	.USPHeading {
		font-size: 28px;
		
		@media #{var.$query-min-lg} {
			font-size: 40px;
		}
	}

	.DestinationEssentialBlock {
		margin-top: 32px;
		
		@media #{var.$query-min-lg} {
			margin-top: 54px;
		}
	}

	.USPList {
		display: flex;
		flex-direction: column;
		row-gap: 80px;
		column-gap: 24px;
		
		@media #{var.$query-min-lg} {
			display: grid;
			grid-template-columns: repeat(auto-fit, 25%);
			justify-content: space-between;
		}
		
		@media #{var.$query-min-xl} {
			grid-template-columns: repeat(auto-fit, 20%);
		}
	}

	.USPItem {
		@apply flex;
		// margin-bottom: 48px;
		// margin-right: 16px;
		// margin-left: 16px;

		@media #{var.$query-min-lg} {
			display: flex;
			margin-bottom: 0;
		}


		.USPItemIcon {
			// flex: 1 1 30%;
			width: 90px;
			height: 117px;
			border-radius: 200px;
			background-position: center;
			background-size: cover;
			background-repeat: no-repeat;
			@apply flex justify-center items-center mx-auto;

			@media #{var.$query-min-lg} {
				width: 202px;
				height: 262px;
			}

			&.teal {
				background-image: url('~/assets/usp/usp-background-pattern-teal.png');
			}

			&.gold {
				background-image: url('~/assets/usp/usp-background-pattern-gold.png');
			}
			
			// Amal gold pattern USP
			&.amal-gold {
				background-image: url('~/assets/usp/amal/amal-islamic-pattern.svg');
				background-color: var(--secondary-gold-base);
			}

			&.blue {
				background-image: url('~/assets/usp/usp-background-pattern-blue.png');
			}
			
			// Amal blue pattern USP
			&.amal-blue {
				background-image: url('~/assets/usp/amal/amal-islamic-pattern.svg');
				background-color: var(--primary-blue-light);
			}

			&.type-2 {
				:deep(svg) {

					@media #{var.$query-min-lg} {
						height: 48px;
					}
				}
			}

			:deep(svg) {
				fill: currentColor;
				width: auto;
				height: 32px;

				@media #{var.$query-min-lg} {
					height: 80px;
				}
			}
		}
		
		// MAG songket pattern USP background
		.USPItemIconSongketPattern {
			background-repeat: repeat;
			background-size: 80px;
			background-position: 6px 56px;
				
			@media #{var.$query-min-lg} {
				background-size: 116px;
				background-position: 101px 68px;
			}
			
			
			// MAG Songket pattern background image
			&.mag-songket-blue {
				--USPBlueOverlay: #{color.scale(var.$primary-midnightgreen-dark, $alpha: -10%)};
				background-image: linear-gradient(90deg, var(--USPBlueOverlay) 100%, var(--USPBlueOverlay) 100%), url('~/assets/usp/mag/songket-pattern-tile.png');
			}
			
			&.mag-songket-gold {
				--USPGoldOverlay: #{color.scale(var.$secondary-gold-base, $alpha: -6%)};
				background-image: linear-gradient(90deg, var(--USPGoldOverlay) 100%, var(--USPGoldOverlay) 100%), url('~/assets/usp/mag/songket-pattern-tile.png');
			}
			
			&.mag-songket-teal {
				--USPTealOverlay: #{color.scale(var.$secondary-teal-base, $alpha: -6%)};
				background-image: linear-gradient(90deg, var(--USPTealOverlay) 100%, var(--USPTealOverlay) 100%), url('~/assets/usp/mag/songket-pattern-tile.png');
			}
		}

		.USPItemBody {
			flex-grow: 1;
			
			@media #{var.$query-max-lg} {
				@apply ml-6;
			}
			
			html[dir="rtl"] & {
				@media #{var.$query-max-lg} {
					@apply mr-6 ml-0;
				}
			}

			.title {
				@apply md:text-lg md:mt-3 mb-4 font-bold break-words;
				word-break: break-word;
			}

			.information {
				@apply mb-7 break-words;
				word-break: break-word;
			}
			
			.USPItemBodyText {
				flex-grow: 1;
				word-break: break-word;
			}

			.description {
				@apply mb-4 break-words;
				word-break: break-word;
			}

			.cta-btn {
				color: var.$primary-blue-base;
				@apply font-semibold;
				transition: .25s ease-in-out;

				&:hover {
					color: var.$primary-blue-light;

					.cta-btn-arrow-icon {
						fill: var.$primary-blue-light;
					}
				}

				.cta-btn-arrow-icon {
					fill: var.$primary-blue-base;
				}
			}
		}
	}
}

.cta-btn {
	.cta-btn-arrow-icon {
		transition: .25s ease-in-out;
	}

	&:hover {
		.cta-btn-arrow-icon {
			@apply transform translate-x-0.5;
		}
	}
	
	html[dir="rtl"] & {
		.cta-btn-arrow-icon {
			@apply transform -rotate-180;
		}
		
		&:hover {
			.cta-btn-arrow-icon {
				@apply transform -translate-x-0.5 -rotate-180;
			}
		}
		
	}
}

.USPBodyItemTextLeft {
	:deep() {
		
		h1, h2, h3, h4, h5, h6, p, li,
		blockquote, dl, dd, hr, figure,
		pre, div[style*="text-align"] {
			text-align: left !important;
		}
	}
}

.USPBodyItemTextRight {
	:deep() {
		
		h1, h2, h3, h4, h5, h6, p, li,
		blockquote, dl, dd, hr, figure,
		pre, div[style*="text-align"] {
			text-align: right !important;
		}
	}
}

.USPBodyItemTextCenter {
	:deep() {
		
		h1, h2, h3, h4, h5, h6, p, li,
		blockquote, dl, dd, hr, figure,
		pre, div[style*="text-align"] {
			text-align: center !important;
		}
	}
}

.USPBodyItemEnhanced {
	:deep() {
		
		h1, h2, h3, h4, h5, h6 {
			margin-top: 24px !important;
			margin-bottom: 16px !important;
			font-weight: 600;
			
			@media #{var.$query-max-lg} {
				margin-top: 12px !important;
				margin-bottom: 12px !important;
			}
		}
	}
}

// MAG stylings
[data-use-theme="MAG"] {
	
	// MAG songket pattern USP background
	.USPItem {
		
		// MAG USP CTA link color
		.USPItemBody {
			.cta-btn {
				color: var(--primary-midnightgreen-base);
				
				.cta-btn-arrow-icon {
					fill: var(--primary-midnightgreen-base);
				}
				
				&:hover {
					color: var(--primary-midnightgreen-light);
				
					.cta-btn-arrow-icon {
						fill: var(--primary-midnightgreen-light);
					}
				}
				
				&:active {
					color: var(--primary-midnightgreen-extralight);
					
					.cta-btn-arrow-icon {
						fill: var(--primary-midnightgreen-extralight);
					}
				}
			}
		}
	}
	
}

.USP[data-use-theme="firefly"] {
	.USPList {
		justify-content: center;
	}
	
	.USPItem {

		.USPItemIcon {
			background: var(--secondary-firefly-orange-extralight);
			width: 125px;
			height: 125px;
			border-radius: 9999px 9999px 0 9999px;
			box-shadow: 8px 8px 0px 4px var(--secondary-firefly-orange-light) inset;

			@media #{var.$query-max-md} {
				width: 90px;
				height: 90px;
			}

			:deep(svg) {
				width: 64px;
				height: auto;

				@media #{var.$query-max-md} {
					width: 40px;
				}
			}
		}

		.USPItemBody {
			.cta-btn {
				color: var(--primary-firefly-orange-base);
				
				.cta-btn-arrow-icon {
					fill: var(--primary-firefly-orange-base);
				}
				
				&:hover {
					color: var(--secondary-firefly-orange-medium);
				
					.cta-btn-arrow-icon {
						fill: var(--secondary-firefly-orange-medium);
					}
				}
				
				&:active {
					color: var(--secondary-firefly-orange-extralight);
					
					.cta-btn-arrow-icon {
						fill: var(--secondary-firefly-orange-extralight);
					}
				}
			}
		}
	}
}
</style>
